import { Service } from "@roc/feature-app-core";

const url = {
  UPDATE_SERVICER_FEES: '/api/v1/loan/saveServicerFeesJson',
  UPDATE_FORECLOSURE_SERVICER_FEES: '/api/v1/loan/saveForeclosureServicerFees',
  UPDATE_PAYOFF_REQUEST_DETAILS: '/api/v1/loan/updatePayoffRequestData',
  UPDATE_SERVICER_PROVIDED_DATA: '/api/v1/loan/updateLoanPayoffDetails',
  GET_PAYOFF_DATA: '/api/v1/loan/getLatestPayoffByLoanId',
  GET_TDS_CHARGES: '/api/v1/loan/getTdsChargesByLoanId',
  GET_FORECLOSURE_SERVICER_FEES: '/api/v1/loan/getForeclosureServicerFees',
  GET_DEFAULT_INTEREST_ALLOCATION: '/api/v1/loan/interestAllocation/getDefaultInterestAllocation',
  GET_FORECLOSURE_INTEREST_ALLOCATION: '/api/v1/loan/interestAllocation/getForeclosureInterestAllocation',
  SAVE_FORECLOSURE_INTEREST_ALLOCATION: '/api/v1/loan/interestAllocation/saveForeclosureInterestAllocation',
  SAVE_DEFAULT_INTEREST_ALLOCATION: '/api/v1/loan/interestAllocation/saveDefaultInterestAllocation',
  GENERATE_CEMA_DOCS: '/api/v1/closingDocs/cemaDocs/generateCEMADocs',
  GET_PAYOFF_BY_ID: '/api/v1/loan/getPayoffById',
  REQUEST_PAYOFF: '/api/v1/loan/requestPayoffV3',
  DOWNLOAD_PAYOFF_STATEMENT_PDF: '/api/v1/loan/payoffCalculator/downloadPayoffStatementPdf',
  SAVE_PAYOFF_DATA: '/api/v1/loan/payoffCalculator/savePayoffData',
  SAVE_REVENUE_DATA: '/api/v1/loan/payoffCalculator/saveRevenueAdjustments',
  RECALCULATE_REVENUE_DATA_FOR_LOAN_V2: '/api/v1/loan/payoffCalculator/recalculateRevenueDataForLoanV2',
  GET_PAYOFF_CALCULATOR_DATA: '/api/v1/loan/payoffCalculator/getPayoffCalculatorData',
  GET_PRE_PAYOFF_CALCULATOR_DATA: '/api/v1/loan/payoffCalculator/getPrePayoffCalculatorData',
  DOWNLOAD_PAYOFF_EXCEL: '/api/v1/loan/payoffCalculator/downloadPayoffReport',
  DOWNLOAD_CEMA_DOCS: '/api/v1/closingDocs/cemaDocs/downloadCEMADocs',
  SAVE_PAYOFF_WAIVER_REQUEST: '/api/v1/loan/payoffWaivers/savePayoffWaiverRequest',
  GET_ACTIVE_PAYOFF_WAIVER_REQUEST: '/api/v1/loan/payoffWaivers/getActivePayoffWaiverRequest',
  APPROVE_PAYOFF_WAIVER_ITEM: '/api/v1/loan/payoffWaivers/approvePayoffWaiverItem',
  REJECT_PAYOFF_WAIVER_ITEM: '/api/v1/loan/payoffWaivers/rejectPayoffWaiverItem',
  UPDATE_PAYOFF_WAIVER_REQUEST_STATUS: '/api/v1/loan/payoffWaivers/updatePayoffWaiverRequestStatus',
  GET_PA_RECONCILIATION_DATA: '/api/v1/loan/specialServicing/getPaReconciliationData',
}

export class PayoffService extends Service {
  url;
  constructor() {
    super();
    this.url = url;
  }

  getTdsCharges(loanId: number) {
    return this.get(this.url.GET_TDS_CHARGES, { loanId });
  }

  getForeclosureCharges(loanId: number) {
    return this.get(this.url.GET_FORECLOSURE_SERVICER_FEES, { loanId });
  }

  saveServicerFees(loanId, data) {
    return this.post(this.url.UPDATE_SERVICER_FEES + `?loanId=${loanId}`, data);
  }

  saveForeclosureServicerFees(loanId, data) {
    return this.post(this.url.UPDATE_FORECLOSURE_SERVICER_FEES + `?loanId=${loanId}`, data);
  }

  updatePayoffRequestDetails(data) {
    return this.put(this.url.UPDATE_PAYOFF_REQUEST_DETAILS, data);
  }

  updateServicerProvidedData(data) {
    return this.post(this.url.UPDATE_SERVICER_PROVIDED_DATA, data);
  }

  getPayoffData(loanId: number) {
    return this.get(this.url.GET_PAYOFF_DATA + `/${loanId}`);
  }

  getDefaultInterestAllocation(loanId: number) {
    return this.get(url.GET_DEFAULT_INTEREST_ALLOCATION, { loanId });
  }

  getForeclosureInterestAllocation(loanId: number) {
    return this.get(url.GET_FORECLOSURE_INTEREST_ALLOCATION, { loanId });
  }

  saveForeclosureInterestAllocation(loanId, data) {
    return this.post(`${url.SAVE_FORECLOSURE_INTEREST_ALLOCATION}?loanId=${loanId}`, data);
  };

  saveDefaultInterestAllocation(data) {
    return this.post(url.SAVE_DEFAULT_INTEREST_ALLOCATION, data);
  };

  generateCemaDocs(data) {
    return this.put(url.GENERATE_CEMA_DOCS, data);
  }

  downloadCemaDocs(data) {
    return this.post(url.DOWNLOAD_CEMA_DOCS, data, {}, { responseType: 'arraybuffer' });
  };

  getPayoffById(loanId, payoffId) {
    return this.get(url.GET_PAYOFF_BY_ID, { loanId, payoffId });
  }

  downloadPayoffStatementPdf(loanId: number, payoffId: number | null, date: string | null) {
    return this.get(url.DOWNLOAD_PAYOFF_STATEMENT_PDF, {
      loanId,
      payoffId,
      date,
    }, {
      responseType: 'arraybuffer',
    });
  }

  savePayoffData(payoffData) {
    return this.post(url.SAVE_PAYOFF_DATA, payoffData);
  }

  saveRevenueAdjustments(revenueData) {
    return this.post(url.SAVE_REVENUE_DATA, revenueData);
  }

  recalculateRevenueDataForLoanV2(loanId) {
    return this.post(`${url.RECALCULATE_REVENUE_DATA_FOR_LOAN_V2}?loanId=${loanId}`, {});
  };

  getPayoffCalculatorData(loanId, payoffId) {
    return this.get(url.GET_PAYOFF_CALCULATOR_DATA, { loanId, payoffId });
  }

  getPrePayoffCalculatorData(loanId, date) {
    return this.get(url.GET_PRE_PAYOFF_CALCULATOR_DATA, { loanId, date });
  }

  getPaReconciliationData(loanId: number) {
    return this.get(url.GET_PA_RECONCILIATION_DATA, { loanId });
  }

  requestPayoff(body: any) {
    return this.put(url.REQUEST_PAYOFF, body);
  }

  downloadPayoffExcel(type: string, loanId: number, payoffId: number | null, date: string | null) {
    return this.get(url.DOWNLOAD_PAYOFF_EXCEL, {
      type,
      loanId,
      payoffId,
      date,
    }, {
      responseType: 'arraybuffer',
    });
  }

  savePayoffWaiverRequest(data) {
    return this.put(url.SAVE_PAYOFF_WAIVER_REQUEST, data);
  }

  getActivePayoffWaiverRequest(loanId, payoffId) {
    return this.get(url.GET_ACTIVE_PAYOFF_WAIVER_REQUEST, { loanId, payoffId });
  }

  approvePayoffWaiverItem(loanId, payoffId, payoffWaiverItemId) {
    return this.post(`${url.APPROVE_PAYOFF_WAIVER_ITEM}?loanId=${loanId}&payoffId=${payoffId}&payoffWaiverItemId=${payoffWaiverItemId}`, {});
  }

  rejectPayoffWaiverItem(loanId, payoffId, payoffWaiverItemId, comment) {
    return this.post(
      `${url.REJECT_PAYOFF_WAIVER_ITEM}?loanId=${loanId}&payoffId=${payoffId}&payoffWaiverItemId=${payoffWaiverItemId}&comment=${comment}`,
      {}
    );
  }

  updatePayoffWaiverRequestStatus(payoffWaiverRequestId, loanTaskId, taskId, status) {
    return this.post(`${url.UPDATE_PAYOFF_WAIVER_REQUEST_STATUS}?payoffWaiverRequestId=${payoffWaiverRequestId}&loanTaskId=${loanTaskId}&taskId=${taskId}&status=${status}`, {});
  }
}